// AutoRemindToggle.tsx (Oct 20, 2026)
//
// The Auto-remind switch under the Send Invoice panel's delivery history. Greg (Oct 15): no automated
// past-due reminders by default; Shannon turns them on for one client once she has confirmed they are
// not waiting on an insurance check. The engine (homeowner-comms touch T8) then texts the pay link 7,
// 14 and 21 days after the final invoice went out, and stops by itself when money lands (migration
// homeowner_comms_t8). This component writes deal_comm_settings; the schedule shown is
// src/lib/autoRemind.ts.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "../../context/AuthContext";
import { fetchEdge, readEdgeJson } from "../../lib/edgeError";
import { SUPABASE_URL } from "../../lib/supabase";
import {
  autoRemindKind,
  enableBlock,
  fmtDay,
  fmtWhen,
  nextReminder,
  pickAnchor,
  projectSchedule,
  reminderSteps,
  skipReasonText,
  stopReasonText,
  REMIND_STAGES,
  type ReminderDelivery,
  type ReminderRow,
  type ReminderSettings,
  type StepView,
} from "../../lib/autoRemind";
import { etDate } from "../../lib/weeklySpiffs";

/** Who may turn reminders on (deal_comm_settings' write policy). */
const EDIT_ROLES = new Set(["admin", "finance", "sales_director", "director_of_operations"]);

const SETTINGS_COLS =
  "deal_id,auto_remind,auto_remind_anchor_delivery_id,auto_remind_enabled_by,auto_remind_enabled_at,auto_remind_paused_at,auto_remind_stopped_at,auto_remind_stop_reason,mute_all,updated_by";

interface DealBits {
  stage: string | null;
  is_retail: boolean | null;
  supplement_approved_date: string | null;
  homeowner_name: string | null;
}

/** A preview from the engine: the first reminder's words, or why it wouldn't go. */
interface Preview {
  sms?: string | null;
  skip?: string;
  error?: string;
}

/** Reasons the engine gives that mean the reminders would never go out. */
const PREVIEW_BLOCKS = new Set(["invoice_stale", "no_invoice_link", "no_balance", "paid", "no_anchor"]);

const shortDate = (iso: string) => fmtDay(etDate(new Date(iso)));

export function AutoRemindToggle({ supabase, dealId }: { supabase: SupabaseClient; dealId: string }) {
  const { member, role, isSuperUser } = useAuth();
  const canEdit = isSuperUser || EDIT_ROLES.has(role ?? "");

  const [deal, setDeal] = useState<DealBits | null>(null);
  const [deliveries, setDeliveries] = useState<ReminderDelivery[]>([]);
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const [rows, setRows] = useState<ReminderRow[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [serverBlock, setServerBlock] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [d, dl, cs, t8] = await Promise.all([
      supabase.from("deals").select("stage,is_retail,supplement_approved_date,homeowner_name").eq("id", dealId).maybeSingle(),
      supabase.from("deal_invoice_deliveries").select("id,sent_at,supplement_approved_amount,revoked_at,paid_at")
        .eq("deal_id", dealId).order("sent_at", { ascending: false }),
      supabase.from("deal_comm_settings").select(SETTINGS_COLS).eq("deal_id", dealId).maybeSingle(),
      supabase.from("homeowner_comm_sends").select("ref,status,skip_reason,sent_at,scheduled_for,created_at")
        .eq("deal_id", dealId).eq("comm_key", "t8"),
    ]);
    const firstErr = d.error ?? dl.error ?? cs.error ?? t8.error;
    if (firstErr) {
      setLoadErr(firstErr.message);
      setLoaded(true);
      return;
    }
    setLoadErr(null);
    setDeal((d.data as DealBits | null) ?? null);
    setDeliveries((dl.data ?? []) as ReminderDelivery[]);
    const s = (cs.data as ReminderSettings | null) ?? null;
    setSettings(s);
    setRows((t8.data ?? []) as ReminderRow[]);
    const ids = [s?.auto_remind_enabled_by, s?.updated_by].filter((x): x is string => !!x);
    if (ids.length) {
      const { data: people } = await supabase.from("team_roster").select("id,full_name").in("id", ids);
      const m: Record<string, string> = {};
      for (const p of (people ?? []) as { id: string; full_name: string | null }[]) m[p.id] = (p.full_name ?? "").split(/\s+/)[0] || "someone";
      setNames(m);
    }
    setLoaded(true);
  }, [supabase, dealId]);

  useEffect(() => { void load(); }, [load]);

  // Live: the engine sending a reminder or stopping the cadence, a new invoice, a stage change.
  useEffect(() => {
    const filter = `deal_id=eq.${dealId}`;
    const ch = supabase
      .channel(`auto-remind-${dealId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "deal_comm_settings", filter }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "homeowner_comm_sends", filter }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "deal_invoice_deliveries", filter }, () => void load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "deals", filter: `id=eq.${dealId}` }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [supabase, dealId, load]);

  const now = new Date();
  const kind = autoRemindKind(settings);
  const candidate = useMemo(() => (deal ? pickAnchor(deliveries, deal) : null), [deliveries, deal]);
  const activeAnchor = settings?.auto_remind_anchor_delivery_id
    ? deliveries.find((x) => x.id === settings.auto_remind_anchor_delivery_id) ?? null
    : null;
  const localBlock = deal ? enableBlock(deal.stage, candidate, now) : null;

  // Nothing to say on a deal that never had reminders and is past or before the reminder stages
  // (except the supps stages right before Collecting, where the switch explains when it opens).
  if (!loaded) return null;
  if (loadErr) return <div className="mt-2 text-[11px] text-slate-400">Auto-remind couldn't load: {loadErr}</div>;
  if (!deal) return null;
  const stage = deal.stage ?? "";
  if (kind === "off" && !REMIND_STAGES.has(stage) && !["ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims"].includes(stage)) {
    return null;
  }

  async function edgeToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  }

  async function openConfirm() {
    if (!candidate) return;
    setConfirmOpen(true);
    setErr(null);
    setServerBlock(null);
    setPreview(null);
    setChecking(true);
    const nowIso = new Date().toISOString();
    // The money rules, from the same database function the engine uses; and the first reminder's words.
    const first = projectSchedule(candidate.sent_at, rows, candidate.id, new Date())[0];
    const [stop, pv] = await Promise.all([
      supabase.rpc("hc_t8_stop_reason", { p_deal_id: dealId, p_now: nowIso, p_anchor_id: candidate.id, p_enabled_at: nowIso }),
      (async (): Promise<Preview> => {
        try {
          const res = await fetchEdge(`${SUPABASE_URL}/functions/v1/homeowner-comms`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${await edgeToken()}` },
            body: JSON.stringify({ mode: "preview", deal_id: dealId, comm_key: "t8", anchor_id: candidate.id, step: first?.step ?? 7, force: true }),
          });
          return await readEdgeJson<Preview>(res);
        } catch (e) {
          return { error: (e as Error).message };
        }
      })(),
    ]);
    if (stop.error) setServerBlock(`Couldn't check: ${stop.error.message}`);
    else if (stop.data) {
      const reason = String(stop.data);
      setServerBlock(
        reason === "completed"
          ? `All three reminders for the ${shortDate(candidate.sent_at)} invoice have gone. Send an updated invoice to start again.`
          : `Can't turn on: ${stopReasonText(reason)}.`,
      );
    } else if (pv.skip && PREVIEW_BLOCKS.has(pv.skip)) {
      setServerBlock(`Can't turn on: ${skipReasonText(pv.skip)}.`);
    }
    setPreview(pv);
    setChecking(false);
  }

  async function logActivity(title: string, description: string, extra: Record<string, unknown> = {}) {
    const { error } = await supabase.from("activity_log").insert({
      deal_id: dealId, activity_type: "system_event", title, description, performed_by: member?.id ?? null,
      metadata: { source: "auto_remind", ...extra },
    });
    if (error) console.warn("auto-remind activity_log insert failed:", error.message);
  }

  async function turnOn() {
    if (!candidate || !member?.id) return;
    setSaving(true);
    setErr(null);
    const at = new Date().toISOString();
    const { error } = await supabase.from("deal_comm_settings").upsert(
      {
        deal_id: dealId,
        auto_remind: true,
        auto_remind_anchor_delivery_id: candidate.id,
        auto_remind_enabled_by: member.id,
        auto_remind_enabled_at: at,
        auto_remind_paused_at: null,
        auto_remind_stopped_at: null,
        auto_remind_stop_reason: null,
        updated_by: member.id,
        updated_at: at,
      },
      { onConflict: "deal_id" },
    );
    setSaving(false);
    if (error) { setErr(`Couldn't turn on: ${error.message}`); return; }
    setConfirmOpen(false);
    void logActivity("Auto-remind turned on", `Balance reminders 7, 14 and 21 days after the ${shortDate(candidate.sent_at)} invoice.`, { anchor_delivery_id: candidate.id });
    void load();
  }

  async function update(patch: Record<string, unknown>, title: string, description: string) {
    if (!member?.id) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase.from("deal_comm_settings")
      .update({ ...patch, updated_by: member.id, updated_at: new Date().toISOString() })
      .eq("deal_id", dealId);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    void logActivity(title, description);
    void load();
  }

  const pause = () => update({ auto_remind_paused_at: new Date().toISOString() }, "Auto-remind paused", "Balance reminders paused.");
  const resume = () => update({ auto_remind_paused_at: null }, "Auto-remind resumed", "Balance reminders resumed.");
  const stop = () => {
    if (!confirm("Stop the balance reminders for this client?")) return;
    void update(
      { auto_remind: false, auto_remind_stopped_at: new Date().toISOString(), auto_remind_stop_reason: "stopped_by_staff" },
      "Auto-remind stopped",
      "Balance reminders stopped.",
    );
  };

  const who = (id: string | null | undefined) => (id && names[id]) || null;
  const steps: StepView[] = activeAnchor
    ? reminderSteps(activeAnchor.sent_at, rows, activeAnchor.id, now, kind === "on")
    : [];
  const next = nextReminder(settings, activeAnchor?.sent_at ?? null, rows, now);
  const projected = candidate ? projectSchedule(candidate.sent_at, rows, candidate.id, now) : [];

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[12px]">
      {/* ── Off ── */}
      {kind === "off" && !confirmOpen && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => void openConfirm()}
            disabled={!canEdit || !!localBlock || saving}
            className="font-semibold px-3 py-1.5 rounded-lg border border-bardin-orange/40 text-bardin-orange bg-bardin-orange/5 hover:bg-bardin-orange/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={localBlock ?? (canEdit ? "Text a reminder with their pay link 7, 14 and 21 days after the invoice" : "Finance or admin only")}
          >
            🔔 Auto-remind this client
          </button>
          <span className="text-[11px] text-slate-500">
            {localBlock ?? "Off. Reminders only go out on clients you turn on."}
          </span>
        </div>
      )}

      {/* ── Confirm ── */}
      {confirmOpen && candidate && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Auto-remind this client</div>
          <p className="text-slate-700 leading-snug">
            Only turn this on once you've confirmed <b>{deal.homeowner_name ?? "this homeowner"}</b> isn't waiting on an
            insurance check. Texts a reminder with their pay link 7, 14 and 21 days after the final invoice was sent.
            Stops by itself when a payment is logged or the balance hits $0.
          </p>
          <div className="text-[11px] text-slate-600 bg-slate-50 rounded px-2.5 py-1.5">
            From the {shortDate(candidate.sent_at)} invoice:{" "}
            {projected.length
              ? projected.map((p) => `reminder ${p.step === 7 ? 1 : p.step === 14 ? 2 : 3} ${fmtWhen(p.date, now)}`).join(" · ")
              : "no reminder is left to send for this invoice."}
            <span className="text-slate-400"> (10 AM–6 PM, never on Sunday, at least 4 days apart)</span>
          </div>
          {checking && <div className="text-[11px] text-slate-400">Checking…</div>}
          {!checking && preview?.sms && (
            <div className="text-[11px] text-slate-600">
              <div className="font-semibold text-slate-500 mb-0.5">The first one reads:</div>
              <div className="whitespace-pre-wrap rounded-lg bg-sky-50 border border-sky-100 px-2.5 py-1.5 text-slate-800">{preview.sms}</div>
            </div>
          )}
          {!checking && serverBlock && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{serverBlock}</div>
          )}
          {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{err}</div>}
          <div className="flex gap-2">
            <button
              onClick={() => void turnOn()}
              disabled={saving || checking || !!serverBlock || projected.length === 0}
              className="flex-1 font-semibold px-3 py-1.5 rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "Turning on…" : "🔔 Turn on reminders"}
            </button>
            <button
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── On / paused ── */}
      {(kind === "on" || kind === "paused") && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-slate-800">
              {kind === "on" ? "🔔 Auto-remind on" : "⏸ Auto-remind paused"}
              <span className="font-normal text-slate-500">
                {kind === "on"
                  ? next ? ` · next: reminder ${next.step === 7 ? 1 : next.step === 14 ? 2 : 3} ${fmtWhen(next.date, now)}` : " · no reminder left to send"
                  : settings?.auto_remind_paused_at ? ` since ${shortDate(settings.auto_remind_paused_at)}` : ""}
              </span>
            </div>
            {canEdit && (
              <div className="flex gap-1.5">
                {kind === "on" ? (
                  <button onClick={() => void pause()} disabled={saving} className="px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                    Pause
                  </button>
                ) : (
                  <button onClick={() => void resume()} disabled={saving} className="px-2.5 py-1 rounded-md border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40">
                    Resume
                  </button>
                )}
                <button onClick={stop} disabled={saving} className="px-2.5 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40">
                  Stop
                </button>
              </div>
            )}
          </div>
          <div className="text-[11px] text-slate-500">
            Following the {activeAnchor ? shortDate(activeAnchor.sent_at) : "?"} invoice
            {who(settings?.auto_remind_enabled_by) ? ` · turned on by ${who(settings?.auto_remind_enabled_by)}` : ""}
            {settings?.auto_remind_enabled_at ? ` ${shortDate(settings.auto_remind_enabled_at)}` : ""}
          </div>
          <StepList steps={steps} now={now} />
          {err && <div className="text-[11px] text-red-700">{err}</div>}
        </div>
      )}

      {/* ── Stopped ── */}
      {kind === "stopped" && !confirmOpen && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-slate-700">
              🔕 Auto-remind stopped
              <span className="font-normal text-slate-500">
                {settings?.auto_remind_stopped_at ? ` ${shortDate(settings.auto_remind_stopped_at)}` : ""}: {stopReasonText(settings?.auto_remind_stop_reason)}
                {settings?.auto_remind_stop_reason === "stopped_by_staff" && who(settings?.updated_by) ? ` by ${who(settings?.updated_by)}` : ""}
              </span>
            </div>
            {canEdit && (
              <button
                onClick={() => void openConfirm()}
                disabled={!!localBlock || saving}
                title={localBlock ?? undefined}
                className="px-2.5 py-1 rounded-md border border-bardin-orange/40 text-bardin-orange hover:bg-bardin-orange/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Turn on again
              </button>
            )}
          </div>
          {localBlock && <div className="text-[11px] text-slate-500">{localBlock}</div>}
          <StepList steps={steps.filter((s) => s.status !== "upcoming" && s.status !== "missed")} now={now} />
        </div>
      )}
    </div>
  );
}

function StepList({ steps, now }: { steps: StepView[]; now: Date }) {
  if (!steps.length) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {steps.map((s) => {
        const n = s.step === 7 ? 1 : s.step === 14 ? 2 : 3;
        const text =
          s.status === "sent" ? `✓ ${n} sent ${s.date ? fmtDay(s.date) : ""}`
          : s.status === "skipped" ? `⏭ ${n} skipped`
          : s.status === "failed" ? `✗ ${n} failed`
          : s.status === "waiting" ? `⏳ ${n} going out`
          : s.status === "held" ? `⏸ ${n} held`
          : s.status === "shadow" ? `${n} test only`
          : s.status === "upcoming" ? `${n} ${s.date ? fmtWhen(s.date, now) : `from ${fmtDay(s.due)}`}`
          : `${n} passed over`;
        const cls =
          s.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : s.status === "failed" ? "bg-red-50 text-red-700 border-red-200"
          : s.status === "skipped" || s.status === "missed" ? "bg-slate-50 text-slate-500 border-slate-200"
          : "bg-sky-50 text-sky-700 border-sky-200";
        const title =
          s.status === "skipped" ? `Skipped: ${skipReasonText(s.reason)}. The next reminder still goes.`
          : s.status === "missed" ? "Its week passed while reminders were paused or turned on late."
          : `Reminder ${n} of 3: ${s.step} days after the invoice (due ${fmtDay(s.due)})`;
        return (
          <li key={s.step} className={"px-2 py-0.5 rounded-full border text-[11px] " + cls} title={title}>
            {text}
          </li>
        );
      })}
    </ul>
  );
}

/** The read-only line on a collecting / delinquent card in Finance → Supplements. */
export function AutoRemindChip({
  settings,
  anchorSentAt,
  rows,
}: {
  settings: ReminderSettings | null | undefined;
  anchorSentAt: string | null;
  rows: ReminderRow[];
}) {
  const kind = autoRemindKind(settings);
  if (kind === "off") return null;
  const now = new Date();
  const next = nextReminder(settings, anchorSentAt, rows, now);
  const text =
    kind === "on" ? `🔔 Auto-remind on${next ? ` · next ${fmtWhen(next.date, now)}` : ""}`
    : kind === "paused" ? "⏸ Auto-remind paused"
    : `🔕 Auto-remind stopped: ${stopReasonText(settings?.auto_remind_stop_reason)}`;
  const cls =
    kind === "on" ? "bg-bardin-orange/10 text-bardin-orange"
    : kind === "paused" ? "bg-amber-50 text-amber-700"
    : "bg-slate-100 text-slate-500";
  return (
    <div
      className={"mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded " + cls}
      title="Balance reminders (7, 14 and 21 days after the final invoice). Change it on the Send Invoice panel below."
    >
      {text}
    </div>
  );
}
